import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Building2, Check } from 'lucide-react';
import { useOptimizedHotel } from '@/contexts/OptimizedHotelContext';
import { useToast } from '@/hooks/use-toast';
import { useLanguage } from '@/i18n/context/LanguageContext';

interface HotelSelectionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const HotelSelectionModal = ({ open, onOpenChange }: HotelSelectionModalProps) => {
  const { currentHotel, availableHotels, switchHotel, isLoading } = useOptimizedHotel();
  const { toast } = useToast();
  const { language } = useLanguage();

  if (import.meta.env.DEV) {
    console.log('🏨 HotelSelectionModal: availableHotels count:', availableHotels.length);
    console.log('🏨 HotelSelectionModal: hotels:', availableHotels);
  }

  const handleHotelSelect = async (hotelId: string) => {
    if (currentHotel?.id === hotelId) {
      // Already selected hotel - just close modal
      onOpenChange(false);
      return;
    }

    try {
      if (import.meta.env.DEV) console.log('🏨 Modal: Starting hotel switch to:', hotelId);
      await switchHotel(hotelId);
      
      toast({
        title: language === 'ru' ? "Успешно" : "Success",
        description: language === 'ru' ? "Отель переключен" : "Hotel switched",
      });
      
      // Close modal without page reload - let context handle the switch
      if (import.meta.env.DEV) console.log('🏨 Modal: Hotel switch completed, closing modal');
      onOpenChange(false);
      
    } catch (error: any) {
      console.error('🏨 Modal: Error switching hotel:', error);
      toast({
        title: language === 'ru' ? "Ошибка" : "Error",
        description: error.message || (language === 'ru' ? "Не удалось переключить отель" : "Failed to switch hotel"),
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            {language === 'ru' ? 'Выберите отель' : 'Select Hotel'}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {availableHotels.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Building2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>{language === 'ru' ? 'Доступных отелей не найдено' : 'No available hotels found'}</p>
            </div>
          ) : (
            availableHotels.map((hotel) => (
              <Button
                key={hotel.id}
                variant={currentHotel?.id === hotel.id ? "default" : "outline"}
                className="w-full justify-start h-auto p-4"
                onClick={() => handleHotelSelect(hotel.id)}
                disabled={isLoading}
              >
                <div className="flex items-center gap-3 w-full">
                  <Building2 className="h-5 w-5 flex-shrink-0" />
                  <div className="flex-1 text-left">
                    <div className="font-medium">{hotel.name}</div>
                    <div className="text-xs text-muted-foreground">
                        {language === 'ru' ? 'ID:' : 'ID:'} {hotel.id}
                      </div>
                  </div>
                  {currentHotel?.id === hotel.id && (
                    <Check className="h-4 w-4 text-primary-foreground" />
                  )}
                </div>
              </Button>
            ))
          )}
        </div>
        
        <div className="flex justify-end pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {language === 'ru' ? 'Закрыть' : 'Close'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};